import * as Sentry from '@sentry/browser';
import { createReduxEnhancer } from '@sentry/react';
import cloneDeep from 'lodash.clonedeep';

import { HTTPError, ScribeScene } from 'js/types';
import { LOAD_SCRIBES_SUCCESS, UPDATE_SCENE_THUMBNAIL, UPDATE_SCRIBE_SUCCESS } from 'js/actionCreators/scribeActions';
import { GET_SCRIBE_FOR_PLAYBACK_SUCCESS } from 'js/actionCreators/playbackActions';

import { sentrySensitiveProps } from './sentrySensitiveProps';
import { sentryIgnoreList } from './ignoreList';

interface SentryConfig {
  dsn?: string;
  release?: string;
  environment?: string;
  allowUrls?: Array<string>;
}

export default function initSentry({ dsn, release, environment, allowUrls }: SentryConfig) {
  if (!dsn) {
    console.warn('MONITORING: Missing SENTRY_DSN. Errors will not be sent to Sentry.');
    return;
  }

  Sentry.getCurrentScope().setLevel('error');

  Sentry.init({
    dsn,
    release,
    environment,
    integrations: [Sentry.browserTracingIntegration()],
    allowUrls,
    debug: import.meta.env.DEV,
    beforeBreadcrumb(breadcrumb, _hint) {
      if (
        (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') &&
        (breadcrumb.data?.url?.startsWith('blob:') || breadcrumb.data?.url?.startsWith('data:'))
      ) {
        breadcrumb.data.url = '[Filtered by Sentry beforeBreadcrumb]';
      }
      return breadcrumb;
    }
  });
}

export function initSentryReduxEnhancer() {
  return createReduxEnhancer({
    stateTransformer: () => null,
    actionTransformer: action => {
      let reduxAction = cloneDeep(action);
      for (const name of sentrySensitiveProps) {
        if (reduxAction.hasOwnProperty(name)) {
          reduxAction[name] = '[Filtered by Sentry Redux Enhancer]';
        }
      }
      if (reduxAction.scribe) {
        reduxAction.scribe.thumbnailImage = '[Filtered by Sentry Redux Enhancer]';
        reduxAction.scribe.scenes?.forEach((scene: ScribeScene) => {
          scene.thumbnailImage = '[Filtered by Sentry Redux Enhancer]';
        });
      }
      if (reduxAction.project) {
        reduxAction.project.thumbnailImage = '[Filtered by Sentry Redux Enhancer]';
        reduxAction.project.scenes?.forEach((scene: ScribeScene) => {
          scene.thumbnailImage = '[Filtered by Sentry Redux Enhancer]';
        });
      }
      if (reduxAction.type === UPDATE_SCENE_THUMBNAIL) {
        reduxAction.thumbnail = '[Filtered by Sentry Redux Enhancer]';
      }
      if (reduxAction.type === UPDATE_SCRIBE_SUCCESS) {
        reduxAction.scribe.projectModel.thumbnailImage = '[Filtered by Sentry Redux Enhancer]';
        reduxAction.scribe.projectModel.scenes?.forEach((scene: ScribeScene) => {
          scene.thumbnailImage = '[Filtered by Sentry Redux Enhancer]';
        });
      }
      if (reduxAction.type === GET_SCRIBE_FOR_PLAYBACK_SUCCESS) {
        reduxAction.scribeData.thumbnailImage = '[Filtered by Sentry Redux Enhancer]';
        reduxAction.scribeData.scenes?.forEach((scene: ScribeScene) => {
          scene.thumbnailImage = '[Filtered by Sentry Redux Enhancer]';
        });
      }
      if (reduxAction.type === LOAD_SCRIBES_SUCCESS) {
        reduxAction.scribes = [];
      }

      return reduxAction;
    }
  });
}

export function sendRendererErrorToSentry(error: Error) {
  sendErrorToSentry(error, {
    tags: {
      renderer: true
    }
  });
}

export function sendTimelineErrorToSentry(error: Error) {
  sendErrorToSentry(error, {
    tags: {
      timeline: 'pixi'
    }
  });
}

export function sendErrorToSentry(
  err: HTTPError,
  captureContext?: Sentry.ExclusiveEventHintOrCaptureContext | undefined
) {
  if ((err.httpStatusCode && Number(err.httpStatusCode) < 500) || (err.err && sentryIgnoreList.indexOf(err?.err) >= 0))
    return; // if `err` is from a Http Request only log `server errors`
  let error = err;

  if (!(error instanceof Error)) {
    error = err?.err && typeof err.err === 'string' ? new Error(err?.err) : new Error(JSON.stringify(err));
  }

  Sentry.captureException(error, captureContext);
}

export function setUserContext({ userId }: { userId: string }) {
  Sentry.setUser({ id: userId });
}
